import { type ReactElement, useState } from "react"
import { Link } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { HeroHeader } from "@/components/ui/hero-section-1"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import {
  useCases as aiUseCases,
  featureSpotlights as featureSpotlightsCopy,
  stats as copyStats,
  featureCatalog as featureCatalogCopy,
  enablementHighlights as enablementHighlightsCopy,
  hero as heroCopy,
  type FeatureCard,
  type FeatureSpotlight,
} from "./ai-features.copy"
import {
  Languages,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Zap,
} from "lucide-react"


type UseCaseItem = FeatureSpotlight | FeatureCard

const isFeatureSpotlight = (item: UseCaseItem): item is FeatureSpotlight => "metrics" in item

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring" as const,
      stiffness: 100,
      damping: 15,
    },
  },
}

const AiFeaturesPageContent = (): ReactElement => {
  const [activeTab, setActiveTab] = useState(aiUseCases.tabs[0].id)

  return (
    <div className="min-h-screen bg-white text-slate-900 selection:bg-blue-100 selection:text-blue-900 font-sans">
      <HeroHeader />
      <main className="relative overflow-hidden">
        {/* Background Gradients */}
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute -top-[20%] left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] bg-blue-50/50 rounded-full blur-3xl opacity-60 mix-blend-multiply" />
          <div className="absolute top-[10%] left-[10%] w-[600px] h-[600px] bg-indigo-50/40 rounded-full blur-3xl opacity-50 mix-blend-multiply" />
          <div className="absolute top-[20%] right-[10%] w-[500px] h-[500px] bg-emerald-50/40 rounded-full blur-3xl opacity-50 mix-blend-multiply" />
        </div>

        {/* Hero Section */}
        <section className="relative pt-24 pb-20 lg:pt-32 lg:pb-28">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              className="mx-auto max-w-4xl text-center"
            >
              <motion.div variants={itemVariants} className="mb-6 flex justify-center">
                <Badge className="rounded-full bg-blue-50 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-blue-600 border border-blue-100 shadow-sm">
                  <Sparkles className="mr-2 h-3 w-3 fill-blue-600" />
                  AI-Native OS
                </Badge>
              </motion.div>

              <motion.h1 variants={itemVariants} className="text-5xl font-extrabold tracking-tight text-slate-900 sm:text-6xl lg:text-7xl mb-6">
                <span className="block">{heroCopy.title.split(" that ")[0]}</span>
                <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-500 bg-clip-text text-transparent">
                  {heroCopy.title.split(" that ")[1] ? "that " + heroCopy.title.split(" that ")[1] : "Revenue Partner"}
                </span>
              </motion.h1>

              <motion.p variants={itemVariants} className="mx-auto max-w-2xl text-lg text-slate-600 leading-relaxed mb-10">
                {heroCopy.subtitle}
              </motion.p>

              <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  asChild
                  size="lg"
                  className="h-14 rounded-full bg-blue-600 px-8 text-base font-semibold text-white shadow-lg shadow-blue-500/25 transition-all hover:bg-blue-700 hover:scale-105 hover:shadow-blue-500/40"
                >
                  <Link to="/pricing">
                    {heroCopy.ctas.primaryLabel}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="h-14 rounded-full border-2 border-slate-200 bg-white px-8 text-base font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition-all"
                >
                  <Link to="/stories">{heroCopy.ctas.secondaryLabel}</Link>
                </Button>
              </motion.div>

              {/* Stats Grid */}
              <motion.div
                variants={containerVariants}
                className="mt-20 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:gap-8"
              >
                {copyStats.map((stat) => (
                  <motion.div
                    key={stat.label}
                    variants={itemVariants}
                    className="relative overflow-hidden rounded-2xl bg-white p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-slate-100 group hover:border-blue-100 transition-colors"
                  >
                    <div className="text-3xl font-bold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">{stat.value}</div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{stat.label}</div>
                    <div className="text-xs text-slate-400 leading-snug">{stat.detail}</div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Bento Grid Feature Stack */}
        <section className="py-24 bg-slate-50/50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-16 text-center max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl mb-4">
                The Complete AI Stack
              </h2>
              <p className="text-lg text-slate-600">
                Everything you need to run a modern restaurant, powered by intelligence that works for you.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-7xl mx-auto">
              {/* Large Featured Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="md:col-span-2 md:row-span-2 rounded-[2.5rem] bg-white p-8 lg:p-12 shadow-xl shadow-slate-200/50 border border-slate-100 relative overflow-hidden group"
              >
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full blur-3xl -mr-32 -mt-32 opacity-50 group-hover:opacity-100 transition-opacity duration-700" />

                <div className="relative z-10 h-full flex flex-col">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-blue-600">
                      <Zap className="h-6 w-6" />
                    </span>
                    <span className="text-sm font-bold uppercase tracking-widest text-blue-600">Flagship Feature</span>
                  </div>

                  <h3 className="text-3xl font-bold text-slate-900 mb-4">{heroCopy.productCard.title.replace("- ", "")}</h3>
                  <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                    {heroCopy.productCard.copy}
                  </p>

                  <div className="mt-auto grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                      <div className="font-semibold text-slate-900 mb-1">Rush-Mode UI</div>
                      <div className="text-sm text-slate-500">Optimized for speed during peak hours</div>
                    </div>
                    <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                      <div className="font-semibold text-slate-900 mb-1">Forecasting Copilots</div>
                      <div className="text-sm text-slate-500">Predict demand before it happens</div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Smaller Feature Cards */}
              {featureSpotlightsCopy.slice(0, 2).map((feature, idx) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="rounded-[2rem] bg-white p-8 shadow-lg shadow-slate-200/40 border border-slate-100 hover:border-blue-100 hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">{feature.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {feature.metrics.map((m) => (
                      <span key={m} className="inline-flex items-center rounded-full bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-600 border border-slate-100">
                        {m}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}

              {/* Remaining Feature Cards to fill grid */}
              {featureSpotlightsCopy.slice(2).map((feature, idx) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: (idx + 2) * 0.1 }}
                  className="rounded-[2rem] bg-white p-8 shadow-lg shadow-slate-200/40 border border-slate-100 hover:border-blue-100 hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">{feature.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {feature.metrics.map((m) => (
                      <span key={m} className="inline-flex items-center rounded-full bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-600 border border-slate-100">
                        {m}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases Tabs */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-white via-slate-50/30 to-white pointer-events-none" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <Badge className="mb-4 rounded-full bg-indigo-50 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-indigo-600 border border-indigo-100">
                Use Cases
              </Badge>
              <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl mb-4">How teams use Braise AI</h2>
              <p className="text-lg text-slate-600">
                Crew, guest, and owner workflows share the same intelligence so guidance stays consistent across channels.
              </p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="max-w-6xl mx-auto">
              <div className="flex justify-center mb-12">
                <TabsList className="bg-slate-100 p-1.5 rounded-full border border-slate-200 relative">
                  {aiUseCases.tabs.map((t) => (
                    <TabsTrigger
                      key={t.id}
                      value={t.id}
                      className="rounded-full px-8 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900 data-[state=active]:text-blue-700 transition-colors relative z-10 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                    >
                      {activeTab === t.id && (
                        <motion.div
                          layoutId="active-tab"
                          className="absolute inset-0 bg-white rounded-full shadow-md border border-slate-100/50 -z-10"
                          transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                        />
                      )}
                      {t.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              <AnimatePresence mode="wait">
                {aiUseCases.tabs.map((t) => (
                  <TabsContent key={t.id} value={t.id} className="mt-0 focus-visible:outline-none">
                    {activeTab === t.id && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="text-center mb-10">
                          <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t.blurb}</p>
                        </div>
                        <div className="grid md:grid-cols-3 gap-6">
                          {(t.items as unknown as UseCaseItem[]).map((item, idx) => {
                            const Icon = item.icon
                            return (
                              <motion.div
                                key={item.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-white rounded-3xl p-8 border border-slate-100 shadow-lg shadow-slate-200/30 hover:shadow-xl hover:border-blue-100 transition-all duration-300 group"
                              >
                                {Icon && (
                                  <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-slate-600 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                                    <Icon className="h-6 w-6" />
                                  </div>
                                )}
                                <h3 className="text-lg font-bold text-slate-900 mb-3">{item.title}</h3>
                                <p className="text-slate-600 text-sm leading-relaxed mb-4">{item.description}</p>
                                {isFeatureSpotlight(item) && (
                                  <div className="flex flex-wrap gap-2 mt-auto">
                                    {item.metrics.map((metric) => (
                                      <span key={metric} className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">
                                        <Sparkles className="h-3 w-3" />
                                        {metric}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </motion.div>
                            )
                          })}
                        </div>
                      </motion.div>
                    )}
                  </TabsContent>
                ))}
              </AnimatePresence>
            </Tabs>
          </div>
        </section>

        {/* Feature Catalog Grid */}
        <section className="py-24 bg-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-50/50 via-white to-white pointer-events-none" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid lg:grid-cols-[1fr_1.5fr] gap-12 lg:gap-20">
              <div className="space-y-8">
                <Badge className="rounded-full bg-blue-50 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-blue-600 border border-blue-100">
                  Feature Catalog
                </Badge>
                <h2 className="text-3xl font-bold sm:text-4xl leading-tight text-slate-900">
                  Every AI feature works in harmony
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Menu intelligence, forecasting, and guardrails are synchronized so the same insight powers QR, counter, handheld, and food-truck flows. Nothing is ad hoc.
                </p>

                <div className="bg-slate-50 rounded-3xl p-8 border border-slate-100">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-6">Why it matters</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3 text-slate-700">
                      <ShieldCheck className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>Every feature respects the same safety nets, approvals, and audit trails.</span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-700">
                      <Languages className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
                      <span>Crew- and guest-facing flows stay multi-language without duplicate work.</span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-700">
                      <CheckCircle2 className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5" />
                      <span>AI guidance is explainable - operators see the "why" behind each recommendation.</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                {featureCatalogCopy.map((feature, idx) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-white rounded-3xl p-6 border border-slate-100 shadow-lg shadow-slate-200/30 hover:shadow-xl hover:border-blue-100 transition-all duration-300 group"
                  >
                    <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-slate-600 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                      <feature.icon className="h-5 w-5" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Enablement Section */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-[3rem] bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 p-8 sm:p-12 lg:p-16 text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-full bg-[url('/noise.png')] opacity-[0.03]" />
              <div className="relative z-10 max-w-4xl mx-auto">
                <Badge className="mb-6 rounded-full bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-slate-600 border border-slate-200 shadow-sm">
                  Enablement
                </Badge>
                <h2 className="text-3xl font-bold text-slate-900 sm:text-5xl mb-6">
                  AI guidance with real operators behind it
                </h2>
                <p className="text-xl text-slate-600 mb-12 max-w-2xl mx-auto">
                  Braise specialists ship with the product. They help crews adopt AI safely, review telemetry weekly, and keep every feature aligned with your revenue goals.
                </p>

                <div className="grid md:grid-cols-3 gap-8 mb-12 text-left">
                  {enablementHighlightsCopy.map((highlight) => (
                    <div key={highlight.headline} className="bg-white rounded-3xl p-8 shadow-lg shadow-slate-200/50 border border-slate-100">
                      <div className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">{highlight.eyebrow}</div>
                      <h3 className="text-lg font-bold text-slate-900 mb-3">{highlight.headline}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed">{highlight.copy}</p>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button asChild size="lg" className="h-14 rounded-full bg-slate-900 px-8 text-base font-semibold text-white shadow-xl shadow-slate-900/20 hover:bg-slate-800 hover:scale-105 transition-all">
                    <Link to="/pricing">Choose your plan</Link>
                  </Button>
                  <Button asChild size="lg" variant="ghost" className="h-14 rounded-full px-8 text-base font-semibold text-slate-600 hover:bg-slate-200/50">
                    <Link to="/">Return home</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

const AiFeaturesPage = (): ReactElement => <AiFeaturesPageContent />

export default AiFeaturesPage
